import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLock } from 'react-icons/fa';
import useTranslation from '../../hooks/useTranslation';
import { useAuth } from '../../provider/AuthProvider';
import api from '../../api/api';
import Sidebar from './Sidebar';
import Header from './Header';
import Button from '../../components/ui/Button';
import { showNotification } from './Notification';

/**
 * Standard notice shown wherever a premium AI feature is unavailable because
 * the tenant's AI flag (Client.ai_enabled) is off.
 *
 * variant="page"   — full-height centered card (route-level, via ProtectedRoute)
 * variant="inline" — compact banner for embedding inside an existing layout
 */
const AiDisabledNotice = ({ variant = 'inline' }) => {
  const navigate = useNavigate();
  const { t, dir } = useTranslation();
  const { userdetails } = useAuth();
  const isSuperuser = !!userdetails?.is_superuser;
  const [enabling, setEnabling] = useState(false);

  // A platform superuser can flip the workspace flag from here; everyone
  // else is told to ask one. The provider refetches on company-updated and
  // the route re-renders with the feature on.
  const enableAi = async () => {
    setEnabling(true);
    try {
      await api.patch('/apps/my-apps/features/', { ai_enabled: true });
      showNotification({ key: 'ai.enabled_toast' });
      window.dispatchEvent(new CustomEvent('company-updated', { detail: { source: 'ai-enabled' } }));
    } catch (err) {
      showNotification(err.response?.data?.error || { key: 'common.action_failed' }, 'error');
    } finally {
      setEnabling(false);
    }
  };

  if (variant === 'page') {
    return (
      <div className={`app-container flex h-screen w-screen bg-canvas relative overflow-hidden font-manrope ${dir === 'rtl' ? 'rtl' : 'ltr'}`} dir={dir}>
        <Sidebar />
        <div role="main" className="main-content flex-1 flex flex-col ml-[220px] w-[calc(100%-220px)] transition-all duration-300 group-[.sidebar-collapsed]:ml-[60px] group-[.sidebar-collapsed]:w-[calc(100%-60px)]">
          <Header />
      <div className="flex-1 h-full min-h-[60vh] flex items-center justify-center p-6 bg-canvas">
        <div className="max-w-md w-full p-10 bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100 text-center relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-violet-50 rounded-full blur-3xl opacity-50"></div>
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-50 rounded-full blur-3xl opacity-50"></div>
          <div className="relative z-10">
            <div className="w-24 h-24 bg-violet-50 rounded-3xl flex items-center justify-center mx-auto mb-8 transform rotate-3 shadow-sm">
              <FaLock className="text-violet-500 text-4xl transform -rotate-3" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 mb-4 tracking-tight">
              {t('ai.disabled_title')}
            </h1>
            <p className="text-gray-500 mb-10 leading-relaxed text-lg">
              {isSuperuser ? t('ai.superadmin_hint') : t('ai.disabled_message')}
            </p>
            {isSuperuser && (
              <Button size="lg" block className="mb-4" loading={enabling} onClick={enableAi}>
                {t('ai.enable_for_workspace')}
              </Button>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className={isSuperuser ? "w-full py-4 px-6 bg-gray-50 text-gray-600 rounded-2xl font-bold hover:bg-gray-100 transition-all duration-300 border border-gray-100" : "w-full py-4 px-6 bg-accent-alt text-white rounded-2xl font-bold hover:bg-accent-hover transition-all duration-300 shadow-[0_10px_20px_rgba(26,115,232,0.2)] hover:transform hover:-translate-y-1 active:scale-95"}
            >
              {t('ai.back_to_dashboard')}
            </button>
          </div>
        </div>
      </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 p-4 bg-violet-50/60 border border-violet-100 rounded-xl">
      <span className="shrink-0 w-9 h-9 bg-violet-100 text-violet-500 rounded-lg flex items-center justify-center">
        <FaLock size={14} />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink">{t('ai.disabled_title')}</p>
        <p className="text-[13px] text-text-2 mt-0.5">{t('ai.disabled_message')}</p>
      </div>
    </div>
  );
};

export default AiDisabledNotice;
